import type { Corpus } from '../types';
import { regulatoryCitationHref } from '../utils/regulatoryCitationHref';

type CitationResolution = 'current' | 'relinked' | 'snapshot_only';

interface ApprovalLetterEvidence {
  chunk_id: number | null;
  page_num: number | null;
  snippet: string;
  citation_resolution: CitationResolution;
}

export interface ApprovalLetterResearchResult {
  approval_letter_id: number;
  rank: number;
  score: number;
  viewer_drug_id: number | null;
  viewer_drug_id_snapshot: number;
  viewer_unavailable_reason: 'no_current_owner' | null;
  application_type: string;
  application_number: string;
  submission_type: string;
  submission_number: number | null;
  version_str: string;
  summary: string | null;
  evidence: ApprovalLetterEvidence[];
}

export interface ApprovalLetterResearchJob {
  corpus?: Corpus;
  drugs_found?: number;
  approval_letter_results?: ApprovalLetterResearchResult[];
}

function submissionLabel(result: ApprovalLetterResearchResult): string {
  const type = result.submission_type.toUpperCase();
  if (type === 'ORIG') return 'Original submission';
  if (type === 'SUPPL') return `Supplement ${result.submission_number ?? ''}`.trim();
  return `${result.submission_type} ${result.submission_number ?? ''}`.trim();
}

export default function ApprovalLetterResearchResults({
  job,
}: {
  job: ApprovalLetterResearchJob;
}) {
  const results = [...(job.approval_letter_results ?? [])].sort((left, right) =>
    left.rank - right.rank || left.approval_letter_id - right.approval_letter_id,
  );
  const documentCount = job.drugs_found ?? results.length;

  return (
    <section aria-labelledby="approval-letter-results-heading" className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 id="approval-letter-results-heading" className="text-lg font-semibold text-slate-800">
            FDA Approval Letters
          </h2>
          <p className="text-sm text-slate-500">
            {documentCount} document{documentCount === 1 ? '' : 's'} found
          </p>
        </div>
      </div>

      {results.length === 0 ? (
        <div className="text-sm text-slate-500 bg-slate-50 px-4 py-6 rounded-lg text-center">
          No matching FDA Approval Letter documents found for this research query.
        </div>
      ) : (
        <div className="space-y-4">
          {results.map((result) => {
            const documentHref = regulatoryCitationHref({
              document_type: 'approval_letter',
              drug_id: result.viewer_drug_id,
              document_id: result.approval_letter_id,
            });
            const viewerUnavailable = result.viewer_drug_id == null;

            return (
              <article
                key={result.approval_letter_id}
                data-letter-id={result.approval_letter_id}
                className="rounded-xl border border-slate-200 bg-white px-5 py-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    {documentHref ? (
                      <a href={documentHref} className="text-base font-semibold text-slate-800 hover:text-indigo-700">
                        {result.application_type} {result.application_number}
                      </a>
                    ) : (
                      <span className="text-base font-semibold text-slate-800">
                        {result.application_type} {result.application_number}
                      </span>
                    )}
                    <p className="mt-0.5 text-sm text-slate-500">
                      {submissionLabel(result)} · {result.version_str}
                    </p>
                  </div>
                  <span className="text-xs text-slate-400">Rank {result.rank}</span>
                </div>

                {viewerUnavailable && (
                  <p className="mt-3 text-sm text-slate-500">
                    Viewer unavailable: the current document owner is no longer available.
                  </p>
                )}

                {result.summary && (
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">{result.summary}</p>
                )}

                {result.evidence.length > 0 && (
                  <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                    {result.evidence.map((evidence, index) => {
                      const href = regulatoryCitationHref({
                        document_type: 'approval_letter',
                        drug_id: result.viewer_drug_id,
                        document_id: result.approval_letter_id,
                        ...(evidence.chunk_id != null ? { chunk_id: evidence.chunk_id } : {}),
                        ...(evidence.page_num != null ? { page: evidence.page_num } : {}),
                      });
                      const label = evidence.page_num == null
                        ? 'Supporting evidence'
                        : `Page ${evidence.page_num}`;

                      return (
                        <div key={`${evidence.chunk_id ?? 'snapshot'}-${index}`}>
                          {href ? (
                            <a href={href} className="text-xs font-semibold text-indigo-600 hover:text-indigo-800">
                              {label}
                            </a>
                          ) : (
                            <span className="text-xs font-semibold text-slate-500">{label}</span>
                          )}
                          {evidence.citation_resolution === 'snapshot_only' && (
                            <p className="mt-0.5 text-xs text-amber-700">
                              Saved evidence; highlight unavailable after rebuild.
                            </p>
                          )}
                          <p className="mt-0.5 text-sm leading-relaxed text-slate-600">{evidence.snippet}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
